import React, { useState } from 'react';
import { X, Crown, DollarSign, Check, ExternalLink, Loader2 } from 'lucide-react';
import { createCheckoutSession } from '../utils/api';

interface PremiumUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  selectedShoeId?: string;
  selectedShoeName?: string;
}

const PremiumUpgradeModal: React.FC<PremiumUpgradeModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  selectedShoeId,
  selectedShoeName
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleUpgrade = async () => {
    setLoading(true);
    setError('');

    try {
      const result = await createCheckoutSession(selectedShoeId);

      if (result.success && result.data?.url) {
        // Redirect to Stripe Checkout
        window.location.href = result.data.url;
      } else {
        setError(result.message || 'Failed to start checkout process');
      }
    } catch (err: any) {
      console.error('Checkout error:', err);
      setError(err.message || 'Failed to start checkout process');
    } finally {
      setLoading(false);
    }
  };

  const handleMaybeLater = () => {
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white max-w-md w-full rounded-2xl shadow-2xl overflow-hidden">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-6 text-white relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
          
          <div className="flex items-center space-x-3 mb-3">
            <div className="bg-white/20 rounded-full p-2">
              <Crown className="h-6 w-6" />
            </div>
            <h3 className="text-xl font-bold">Unlock Premium Access</h3>
          </div>
          
          <p className="text-white/90 text-sm">
            Get exclusive access to the best discount links
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Value proposition */}
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
            <div className="flex items-center space-x-3">
              <div className="bg-green-500 rounded-full p-2 flex-shrink-0">
                <DollarSign className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="font-bold text-green-800 text-lg">Save $50-$100</p>
                <p className="text-green-700 text-sm">On every shoe purchase!</p>
              </div>
            </div>
          </div>

          {/* Selected shoe info */}
          {selectedShoeName && (
            <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm">
              <p className="text-gray-600">You're looking at:</p>
              <p className="font-medium text-gray-900 truncate">{selectedShoeName}</p>
            </div>
          )}

          {/* Benefits list */}
          <div className="space-y-3 mb-6">
            <div className="flex items-start space-x-3">
              <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700 text-sm">Access exclusive discount links from our partners</span>
            </div>
            <div className="flex items-start space-x-3">
              <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700 text-sm">Average savings of $50-$100 per purchase</span>
            </div>
            <div className="flex items-start space-x-3">
              <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700 text-sm">Unlimited access to all shoe recommendations</span>
            </div>
            <div className="flex items-start space-x-3">
              <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700 text-sm">Price drop alerts for your favorites</span>
            </div>
          </div>

          {/* Pricing */}
          <div className="text-center mb-6">
            <div className="flex items-baseline justify-center">
              <span className="text-4xl font-bold text-gray-900">$4.99</span>
              <span className="text-gray-500 ml-1">/month</span>
            </div>
            <p className="text-gray-500 text-sm mt-1">Cancel anytime</p>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm mb-4">
              {error}
            </div>
          )}

          {/* CTA buttons */}
          <div className="space-y-3">
            <button
              onClick={handleUpgrade}
              disabled={loading}
              className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <Crown className="h-5 w-5" />
                  <span>Upgrade to Premium</span>
                  <ExternalLink className="h-4 w-4 ml-1" />
                </>
              )}
            </button>
            
            <button
              onClick={handleMaybeLater}
              disabled={loading}
              className="w-full text-gray-600 hover:text-gray-800 font-medium py-2 px-4 transition-colors duration-200"
            >
              Maybe Later
            </button>
          </div>

          {/* Trust badge */}
          <div className="mt-6 pt-4 border-t border-gray-100 text-center">
            <p className="text-xs text-gray-500">
              🔒 Secure payment powered by Stripe
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PremiumUpgradeModal;

